import { useMemo, useState } from 'react'

import {
  useLazyBrandsQuery,
  useLazyGetBrandNamesFromIdsQuery,
} from '../../../../store/api'
import type {
  MetaResponse,
  PaginatedQueryParams,
} from '../../../../store/types'
import type { Brand } from '../../../../store/types/brand'
import type { DropdownOption } from '../../types'
import FilterAsyncPaginateDropdown from '../filter-async-paginate'

type BrandsDropdownProps<B extends boolean> = {
  name?: string
  onChange?: B extends true
    ? (item: DropdownOption[]) => void
    : (item: DropdownOption) => void
  onSubmit?: () => void
  isMulti?: boolean
  isClearable?: boolean
  isCreatable?: boolean
  exclude?: number | number[]
  value?: DropdownOption[]
  styles?: any
  components?: any
  filterValues?: any
}

const brandsToDropdownOptions = (brand: Brand): DropdownOption => ({
  label: brand.name,
  value: brand.id.toString(),
})

export default function BrandsFilterDropdown({
  onChange,
  isClearable = false,
  exclude = undefined,
  value,
  onSubmit,
  filterValues,
}: BrandsDropdownProps<boolean>) {
  const [fetcher] = useLazyBrandsQuery()
  const brandsFetcher = async (params: PaginatedQueryParams) => {
    const res = await fetcher({
      ...params,
      orderBy: 'name',
      orderDirection: 'asc',
    })
    const response: { data?: DropdownOption[]; meta?: MetaResponse<Brand> } = {}
    if (exclude !== undefined) {
      if (Array.isArray(exclude)) {
        const filteredResponse: any = res.data?.data
          .filter((brand: any) => !exclude.includes(brand?.id))
          .map(brandsToDropdownOptions)
        response.data = filteredResponse
      } else {
        const filteredResponse: any = res.data?.data
          .filter((brand: any) => brand?.id !== exclude)
          .map(brandsToDropdownOptions)
        response.data = filteredResponse
      }
    } else {
      response.data = res.data?.data.map(brandsToDropdownOptions)
    }
    response.meta = res.data?.meta

    return response
  }
  const [defaultBrandValues, setDefaultBrandValues] = useState<
    Array<{ label: string; value: string }>
  >([])

  const [getBrandNames, { isLoading }] = useLazyGetBrandNamesFromIdsQuery()

  useMemo(() => {
    if (filterValues) {
      const values = filterValues
        .split(',')
        .map((n: string) => Math.abs(Number(n)))
      if (
        values.length > 0 &&
        !values.every(
          (i: number) =>
            defaultBrandValues.findIndex(
              (item) => item.value === i.toString(),
            ) > -1,
        )
      ) {
        getBrandNames({ ids: filterValues })
          .then((response) => {
            const items = response?.data.map((item: any) => ({
              label: item?.name,
              value: item?.id.toString(),
              isNegative: item?.is_negative,
            }))
            const idList = filterValues
              .split(',')
              .map((item: string) => Math.abs(Number(item)).toFixed())
            items.sort(
              (i1: any, i2: any) =>
                idList.indexOf(i1.value) - idList.indexOf(i2.value),
            )
            setDefaultBrandValues(items)
          })
          .catch((err) => {})
      }
    } else {
      setDefaultBrandValues([])
    }
  }, [filterValues])

  return (
    <FilterAsyncPaginateDropdown
      onSubmit={onSubmit}
      fetcher={brandsFetcher}
      isReady={!isLoading}
      defaultValue={defaultBrandValues}
      onChange={onChange}
      isClearable={isClearable}
      value={value}
    />
  )
}
